/**
 * WebRTC Signaling Hook
 * Manages WebSocket communication for WebRTC signaling
 * Based on backend API: docs/api/WEBRTC_GUIDE.md
 */

import { useEffect, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import type {
  UseWebRTCSignalingOptions,
  UseWebRTCSignalingReturn,
  PeerJoinedPayload,
  PeerLeftPayload,
  OfferReceivedPayload,
  AnswerReceivedPayload,
  IceCandidateReceivedPayload,
} from '@/api/types/webrtc.types';

/**
 * Hook for WebRTC signaling via WebSocket
 *
 * Handles:
 * - Joining/leaving rooms
 * - Sending/receiving SDP offers and answers
 * - Exchanging ICE candidates
 * - Peer joined/left notifications
 *
 * @example
 * ```typescript
 * const signaling = useWebRTCSignaling({
 *   roomName: `${tenantId}-meeting-${meetingId}`,
 *   userId: currentUser.id,
 *   displayName: currentUser.displayName,
 *   onPeerJoined: (peerId, displayName) => {
 *     console.log(`Peer joined: ${displayName}`);
 *   },
 *   onOffer: (sdp, fromUserId) => {
 *     // Handle incoming SDP offer
 *   }
 * });
 *
 * // Join room
 * signaling.joinRoom();
 *
 * // Send offer
 * signaling.sendOffer(localDescription);
 * ```
 */
export function useWebRTCSignaling({
  roomName,
  userId,
  displayName,
  enabled = true,
  onPeerJoined,
  onPeerLeft,
  onOffer,
  onAnswer,
  onIceCandidate,
}: UseWebRTCSignalingOptions): UseWebRTCSignalingReturn {
  // Connect to WebSocket (root namespace '/')
  const { socket, connected } = useWebSocket({
    namespace: '/', // Root namespace as per backend implementation
    enabled,
    autoConnect: true,
  });

  // Join room
  const joinRoom = useCallback(() => {
    if (!socket || !connected) {
      console.warn('Cannot join room: socket not connected');
      return;
    }

    console.log(`🚪 Joining room: ${roomName}`);
    socket.emit('webrtc:join-room', {
      room_name: roomName,
      user_id: userId,
      display_name: displayName,
    });
  }, [socket, connected, roomName, userId, displayName]);

  // Leave room
  const leaveRoom = useCallback(() => {
    if (!socket || !connected) {
      return;
    }

    console.log(`👋 Leaving room: ${roomName}`);
    socket.emit('webrtc:leave-room', {
      room_name: roomName,
      user_id: userId,
    });
  }, [socket, connected, roomName, userId]);

  // Send SDP offer
  const sendOffer = useCallback(
    (sdp: RTCSessionDescriptionInit) => {
      if (!socket || !connected) {
        console.warn('Cannot send offer: socket not connected');
        return;
      }

      console.log(`📤 Sending offer to room: ${roomName}`);
      socket.emit('webrtc:offer', {
        room_name: roomName,
        sdp,
        from_user_id: userId,
      });
    },
    [socket, connected, roomName, userId]
  );

  // Send SDP answer
  const sendAnswer = useCallback(
    (sdp: RTCSessionDescriptionInit) => {
      if (!socket || !connected) {
        console.warn('Cannot send answer: socket not connected');
        return;
      }

      console.log(`📤 Sending answer to room: ${roomName}`);
      socket.emit('webrtc:answer', {
        room_name: roomName,
        sdp,
        from_user_id: userId,
      });
    },
    [socket, connected, roomName, userId]
  );

  // Send ICE candidate
  const sendIceCandidate = useCallback(
    (candidate: RTCIceCandidateInit) => {
      if (!socket || !connected) {
        console.warn('Cannot send ICE candidate: socket not connected');
        return;
      }

      console.log(`📤 Sending ICE candidate to room: ${roomName}`);
      socket.emit('webrtc:ice-candidate', {
        room_name: roomName,
        candidate,
        from_user_id: userId,
      });
    },
    [socket, connected, roomName, userId]
  );

  // Listen for signaling events
  useEffect(() => {
    if (!socket || !connected) return;

    console.log('🎧 Setting up WebRTC signaling event listeners');

    // Peer joined
    const handlePeerJoined = (data: PeerJoinedPayload) => {
      console.log(`👤 Peer joined: ${data.display_name} (${data.peer_id})`);
      onPeerJoined?.(data.peer_id, data.display_name);
    };

    // Peer left
    const handlePeerLeft = (data: PeerLeftPayload) => {
      console.log(`👋 Peer left: ${data.peer_id}`);
      onPeerLeft?.(data.peer_id);
    };

    // Received SDP offer
    const handleOffer = (data: OfferReceivedPayload) => {
      console.log(`📩 Received offer from: ${data.from_user_id}`);
      onOffer?.(data.sdp, data.from_user_id);
    };

    // Received SDP answer
    const handleAnswer = (data: AnswerReceivedPayload) => {
      console.log(`📩 Received answer from: ${data.from_user_id}`);
      onAnswer?.(data.sdp, data.from_user_id);
    };

    // Received ICE candidate
    const handleIceCandidate = (data: IceCandidateReceivedPayload) => {
      console.log(`📩 Received ICE candidate from: ${data.from_user_id}`);
      onIceCandidate?.(data.candidate, data.from_user_id);
    };

    // Register event listeners
    socket.on('webrtc:peer-joined', handlePeerJoined);
    socket.on('webrtc:peer-left', handlePeerLeft);
    socket.on('webrtc:offer', handleOffer);
    socket.on('webrtc:answer', handleAnswer);
    socket.on('webrtc:ice-candidate', handleIceCandidate);

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up WebRTC signaling event listeners');
      socket.off('webrtc:peer-joined', handlePeerJoined);
      socket.off('webrtc:peer-left', handlePeerLeft);
      socket.off('webrtc:offer', handleOffer);
      socket.off('webrtc:answer', handleAnswer);
      socket.off('webrtc:ice-candidate', handleIceCandidate);
    };
  }, [
    socket,
    connected,
    onPeerJoined,
    onPeerLeft,
    onOffer,
    onAnswer,
    onIceCandidate,
  ]);

  return {
    isConnected: connected,
    joinRoom,
    leaveRoom,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
  };
}
